import { Modal } from 'components/common/Modal/Modal';
import i18n from 'i18n';
import styles from './siteAnnouncementDetailModal.module.scss';
import { SiteAnnouncement } from 'core/siteAnnouncement/SiteAnnouncement';
import { useCallback, useEffect, useState } from 'react';
import { SiteAnnouncementContent } from 'components/SiteAnnouncement/SiteAnnouncementContent';
import { SiteAnnouncementTypeCapsule } from './SiteAnnouncementTypeCapsule';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import {
  faDownload,
  faFile,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileWord
} from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classnames from 'classnames/bind';
import axios from 'axios';
import { formatBytes } from 'utils/StringUtil';
import { downloadFromUrl, getFileNameFromUrl } from 'utils/DownloadUtils';

const cx = classnames.bind(styles);

const getExtension = (fileName: string) =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

const ATTACHMENT_ICON: Record<string, IconProp> = {
  pdf: faFilePdf,
  doc: faFileWord,
  docx: faFileWord,
  ppt: faFilePowerpoint,
  pptx: faFilePowerpoint,
  png: faFileImage,
  jpg: faFileImage,
  jpeg: faFileImage
};
const ATTACHMENT_TYPE_LABEL: Record<string, string> = {
  pdf: 'PDF',
  doc: 'DOC',
  docx: 'DOC',
  ppt: 'PPT',
  pptx: 'PPT',
  png: 'PNG',
  jpg: 'JPG',
  jpeg: 'JPG'
};

const SiteAnnouncementAttachmentItem: React.FC<{ url: string }> = ({ url }) => {
  const fileName = getFileNameFromUrl(url);
  const extension = getExtension(fileName);
  const icon = ATTACHMENT_ICON[extension] ?? faFile;
  const typeLabel = ATTACHMENT_TYPE_LABEL[extension] ?? extension.toUpperCase();
  const [size, setSize] = useState<string>();
  const [downloading, setDownloading] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    // size isn't part of the announcement payload; probe it lazily and show it
    // only when the storage host allows the HEAD request
    axios
      .head(url)
      .then((response) => {
        const contentLength = Number(response.headers['content-length']);
        if (active && contentLength > 0) {
          setSize(formatBytes(contentLength));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [url]);

  const onDownload = useCallback(async () => {
    setDownloading(true);
    try {
      await downloadFromUrl(url, fileName);
    } finally {
      setDownloading(false);
    }
  }, [url, fileName]);

  return (
    <div className={styles.attachmentCard}>
      <FontAwesomeIcon
        className={cx('attachmentIcon', { pdf: extension === 'pdf' })}
        icon={icon}
      />
      <div className={styles.attachmentInfo}>
        <div className={styles.attachmentName}>{fileName}</div>
        <div className={styles.attachmentMeta}>
          {size ? `${typeLabel} · ${size}` : typeLabel}
        </div>
      </div>
      <button
        type='button'
        className={styles.downloadBtn}
        onClick={onDownload}
        disabled={downloading}
      >
        <FontAwesomeIcon icon={faDownload} />
        {i18n.t('common.buttons.download')}
      </button>
    </div>
  );
};

export const SiteAnnouncementDetailModal: React.FC<{
  announcement: SiteAnnouncement;
  onClose: () => void;
}> = ({ announcement, onClose }) => {
  const [zoomed, setZoomed] = useState<boolean>(false);
  const attachmentUrls = announcement.attachmentUrls || [];

  return (
    <Modal
      title=''
      className={styles.infoModalContainer}
      dismiss={onClose}
      enableCloseBtn
    >
      {announcement.imageUrl && (
        <div className={styles.imageWrapper} onClick={() => setZoomed(true)}>
          <img
            className={styles.image}
            src={announcement.imageUrl}
            alt={announcement.name}
          />
        </div>
      )}
      <div className={styles.detail}>
        <div className={styles.nameRow}>
          <SiteAnnouncementTypeCapsule type={announcement.type} />
          <div className={styles.name}>{announcement.name}</div>
        </div>
        <div className={styles.publishTime}>
          <FontAwesomeIcon icon={faClock} />
          <span>
            {i18n.t('siteAnnouncementDetailModal.labels.publishedTime', {
              time: announcement.publishTime
            })}
          </span>
        </div>
        <div className={styles.content}>
          <SiteAnnouncementContent
            content={announcement.content}
            macroToHyperlink
          />
        </div>
        {attachmentUrls.length > 0 && (
          <div className={styles.attachmentsSection}>
            <div className={styles.attachmentsTitle}>
              {i18n.t('siteAnnouncementList.headers.attachmentUrls')}
            </div>
            {attachmentUrls.map((attachmentUrl) => (
              <SiteAnnouncementAttachmentItem
                key={attachmentUrl}
                url={attachmentUrl}
              />
            ))}
          </div>
        )}
      </div>
      {zoomed && announcement.imageUrl && (
        <div className={styles.zoomOverlay} onClick={() => setZoomed(false)}>
          <img
            className={styles.zoomImage}
            src={announcement.imageUrl}
            alt={announcement.name}
          />
        </div>
      )}
    </Modal>
  );
};
